import { Container, Grid, GridItem, Stack, Visibility } from "@octopus-energy/coral/atoms"
import { StoryblokComponent, storyblokEditable } from "@storyblok/react"
import { Page } from "@/components/organisms"
import {
    Connector1,
    Connector2,
    Connector3,
    Connector4,
    Connector5,
    ConnectorMobile,
    FadeIn,
    GreenCar,
    GreenFrance,
    GreenHouses,
    GreenHousesFading,
    GreenPylon,
    RenewableRepartition,
    SolarPanelsWithWindmills,
} from "@/features/greenEnergy"
import { type SbBlokGreenEnergy } from "../types"

type DynamicGreenEnergyProps = {
    blok: SbBlokGreenEnergy
}

export function DynamicGreenEnergy({ blok }: DynamicGreenEnergyProps) {
    return (
        <Page
            theme="base900"
            {...storyblokEditable(blok)}
        >
            <Grid
                gridTemplateColumns={{ base: "25% 75%", xl: "repeat(12, 200px)" }}
                gridTemplateRows={{ base: undefined, xl: "repeat(5, auto)" }}
                fullWidth
            >
                {blok.energyDistributed.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 3, xl: 1 }}
                    gridColumnStart={{ base: 2, xl: 3 }}
                    gridRowEnd={{ base: 4, xl: 2 }}
                    gridColumnEnd={{ base: 3, xl: 5 }}
                    alignSelf={{ base: undefined, xl: "flex-end" }}
                    justifySelf="flex-end"
                >
                    <FadeIn>
                        <SolarPanelsWithWindmills />
                    </FadeIn>
                </GridItem>
                {blok.renewableEnergy.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 3, xl: 2 }}
                    gridColumnStart={{ base: 1, xl: 3 }}
                    gridRowEnd={{ base: 4, xl: 3 }}
                    gridColumnEnd={{ base: 2, xl: 5 }}
                    justifySelf={{ base: undefined, xl: "flex-end" }}
                    alignSelf={{ base: "center", xl: "initial" }}
                >
                    <FadeIn delay={1}>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Container marginTop="md">
                                <Connector1 />
                            </Container>
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <ConnectorMobile size="sm" />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                <GridItem
                    gridRowStart={{ base: 1, xl: 2 }}
                    gridColumnStart={{ base: 1, xl: 1 }}
                    gridRowEnd={{ base: 1, xl: 4 }}
                    gridColumnEnd={{ base: 2, xl: 3 }}
                >
                    <FadeIn>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Container
                                position="relative"
                                paddingY="md"
                            >
                                <GreenPylon />
                                <Container
                                    position="absolute"
                                    left="5em"
                                    top="6em"
                                >
                                    <GreenHouses width={250} />
                                </Container>
                            </Container>
                        </Visibility>
                    </FadeIn>
                </GridItem>

                {blok.energyConsumed.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 6, xl: 4 }}
                    gridColumnStart={{ base: 1, xl: 5 }}
                    gridRowEnd={{ base: 7, xl: 5 }}
                    gridColumnEnd={{ base: 3, xl: 5 }}
                    alignSelf={{ base: "center", xl: "initial" }}
                >
                    <FadeIn delay={1}>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Connector2 />
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <ConnectorMobile size="md" />
                        </Visibility>
                    </FadeIn>
                </GridItem>

                {blok.energyBought.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}

                {blok.energyInvest.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 6, xl: 5 }}
                    gridColumnStart={{ base: 1, xl: 2 }}
                    gridRowEnd={{ base: 8, xl: 7 }}
                    gridColumnEnd={{ base: 3, xl: 5 }}
                    justifySelf={{ base: "flex-end", xl: "flex-start" }}
                >
                    <FadeIn>
                        <GreenFrance />
                    </FadeIn>
                </GridItem>
                <GridItem
                    gridRowStart={{ base: 8, xl: 6 }}
                    gridColumnStart={{ base: 1, xl: 1 }}
                    gridRowEnd={{ base: 9, xl: 8 }}
                    gridColumnEnd={{ base: 2, xl: 2 }}
                    alignSelf={{ base: undefined, xl: "center" }}
                    justifySelf={{ base: undefined, xl: "flex-end" }}
                >
                    <FadeIn delay={1}>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Connector3 />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                <GridItem
                    gridRowStart={{ base: 12, xl: 7 }}
                    gridColumnStart={{ base: 2, xl: 2 }}
                    gridRowEnd={{ base: 13, xl: 9 }}
                    gridColumnEnd={{ base: 3, xl: 3 }}
                    justifySelf="center"
                >
                    <FadeIn>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Container paddingTop={{ base: undefined, xl: "lg" }}>
                                <GreenHousesFading />
                            </Container>
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <GreenHousesFading.Small />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                {blok.energyHouseholds.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 12, xl: 7 }}
                    gridColumnStart={{ base: 1, xl: 5 }}
                    gridRowEnd={{ base: 14, xl: 10 }}
                    gridColumnEnd={{ base: 2, xl: 6 }}
                    justifySelf={{ base: undefined, xl: "center" }}
                    alignSelf="center"
                >
                    <FadeIn delay={1}>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Connector4 />
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <ConnectorMobile size="lg" />
                        </Visibility>
                    </FadeIn>
                </GridItem>

                {blok.energyOrigin.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
                <GridItem
                    gridRowStart={{ base: 13, xl: 9 }}
                    gridColumnStart={{ base: 2, xl: 2 }}
                    gridRowEnd={{ base: 14, xl: 11 }}
                    gridColumnEnd={{ base: 3, xl: 3 }}
                    justifySelf="center"
                >
                    <FadeIn>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Container
                                minWidth={300}
                                minHeight={300}
                            >
                                <RenewableRepartition />
                            </Container>
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <RenewableRepartition
                                width={200}
                                height={200}
                            />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                <GridItem
                    gridRowStart={{ base: 19, xl: 11 }}
                    gridColumnStart={{ base: 1, xl: 5 }}
                    gridRowEnd={{ base: 20, xl: 13 }}
                    gridColumnEnd={{ base: 3, xl: 6 }}
                >
                    <FadeIn>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <Stack justifyContent="space-between">
                                <FadeIn delay={1}>
                                    <ConnectorMobile size="md" />
                                </FadeIn>
                                <GreenCar height={190} />
                            </Stack>
                        </Visibility>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <GreenCar />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                <GridItem
                    gridRowStart={{ base: 10, xl: 11 }}
                    gridColumnStart={{ base: 1, xl: 4 }}
                    gridRowEnd={{ base: 11, xl: 12 }}
                    gridColumnEnd={{ base: 2, xl: 5 }}
                >
                    <FadeIn>
                        <Visibility hidden={{ base: true, xl: false }}>
                            <Connector5 />
                        </Visibility>
                        <Visibility hidden={{ base: false, xl: true }}>
                            <ConnectorMobile size="sm" />
                        </Visibility>
                    </FadeIn>
                </GridItem>
                {blok.energyMobility.map((nestedBlok) => (
                    <StoryblokComponent
                        blok={nestedBlok}
                        key={nestedBlok._uid}
                    />
                ))}
            </Grid>
        </Page>
    )
}

export default DynamicGreenEnergy
